<template>
    <div
        :class="`search-container-${mode}`"
        @mousedown="$_resultMouseDown(true)"
        @mouseup="$_resultMouseDown(false)"
        @mouseout="pressed = false"
        ref="searchContainer"
    >
        <!-- Search field -->
        <search-box
            name="searchBox"
            ref="searchBoxInput"
            :info="$t('searchInfo')"
            :autofocus="false"
            :value="localSearchText"
            :extendedClass="`search-bar-all search-bar-${mode}`"
            @focus="$_onFocus"
            @blur="$_onInputBlur"
            @input="$_onInput"
            autocomplete="off"
            :show-save-button="showSaveButton"
            :show-search-icon="showSearchIcon"
            @save="$_onSaveUserSearch"
        />
        <!-- Search results -->
        <div
            class="search-results"
            :class="`search-results-${mode}`"
            v-if="displayResults"
            ref="searchResults"
        >
            <template v-if="searchHasResults">
                <!-- webThemes -->
                <ul>
                    <li
                        v-for="webthemeAtribute in searchSuggestions.webthemes"
                        :key="`${webthemeAtribute.key}${webthemeAtribute.value}`"
                    >
                        <b-link
                            @click="
                                $_openLink({
                                    type: 'webtheme',
                                    data: webthemeAtribute
                                })
                            "
                            v-ng-usage:click="{
                                name: 'searchResult',
                                type: 'webtheme'
                            }"
                            :title="webthemeAtribute.value"
                            :aria-label="$t('searchResultLabelThemeSelection')"
                        >
                            <div class="icon_inline" v-if="!accessibleMode">
                                <div class="cube-icon"></div>
                            </div>
                            <span
                                v-html="
                                    $options.filters.highlight(
                                        webthemeAtribute.value,
                                        searchText
                                    )
                                "
                            ></span>
                        </b-link>
                    </li>
                </ul>
                <!-- Themes -->
                <ul
                    v-for="theme in searchSuggestions.themes"
                    v-bind:key="theme.index"
                >
                    <li
                        v-for="searchAttribute in theme.searchAttributes"
                        v-bind:key="searchAttribute.index"
                    >
                        <b-link
                            @click="
                                $_openLink({
                                    type: 'theme',
                                    data: { theme, searchAttribute }
                                })
                            "
                            v-ng-usage:click="{
                                name: 'searchResult',
                                type: 'theme'
                            }"
                            :title="searchAttribute.value"
                            :aria-label="$t('searchResultLabelThemeLayer')"
                        >
                            <div class="icon_inline" v-if="!accessibleMode">
                                <div class="layer-icon"></div>
                            </div>
                            <span
                                v-html="
                                    $options.filters.highlight(
                                        searchAttribute.value,
                                        searchText
                                    )
                                "
                            ></span>
                            (<span class="theme-name">{{ theme.name }}</span
                            >)
                        </b-link>
                    </li>
                </ul>
                <!-- themeLayerFilter -->
                <ul
                    v-for="theme in searchSuggestions.themeLayerFilter"
                    v-bind:key="theme.index"
                >
                    <li
                        v-for="searchAttribute in theme.searchAttributes"
                        v-bind:key="searchAttribute.index"
                    >
                        <b-link
                            @click="
                                $_openLink({
                                    type: 'themeLayerFilter',
                                    data: { theme, searchAttribute }
                                })
                            "
                            v-ng-usage:click="{
                                name: 'searchResult',
                                type: 'themeLayerFilter'
                            }"
                            :title="searchAttribute.value"
                            :aria-label="$t('searchResultLabelThemeFilter')"
                        >
                            <div class="icon_inline" v-if="!accessibleMode">
                                <div class="cube-icon"></div>
                            </div>
                            <span
                                v-html="
                                    $options.filters.highlight(
                                        searchAttribute.value,
                                        searchText
                                    )
                                "
                            ></span>
                            (<span class="theme-name">{{ theme.name }}</span
                            >)
                        </b-link>
                    </li>
                </ul>
                <!-- feedbacks -->
                <ul
                    v-for="feedback in searchSuggestions.feedbacks"
                    v-bind:key="`feedback_${feedback.id}`"
                >
                    <li
                        v-for="(
                            searchAttribute, index
                        ) in feedback.searchAttributes"
                        v-bind:key="`feedback_${feedback.id}_${index}`"
                    >
                        <b-link
                            @click="
                                $_openLink({
                                    type: 'feedback',
                                    data: { feedback, searchAttribute }
                                })
                            "
                            v-ng-usage:click="{
                                name: 'searchResult',
                                type: 'feedback'
                            }"
                            :title="searchAttribute.value"
                            :aria-label="
                                $t('searchResultLabelFeedbackSelection')
                            "
                        >
                            <div class="icon_inline" v-if="!accessibleMode">
                                <div class="feedback-icon"></div>
                            </div>
                            <span
                                v-html="
                                    $options.filters.highlight(
                                        searchAttribute.value,
                                        searchText
                                    )
                                "
                            ></span>
                            (<span class="theme-name">{{ feedback.name }}</span
                            >)
                        </b-link>
                    </li>
                </ul>
                <!-- feedbackFilter -->
                <ul
                    v-for="feedbackFilter in searchSuggestions.feedbackFilters"
                    v-bind:key="`feedback_${feedbackFilter.id}`"
                >
                    <li
                        v-for="(
                            searchAttribute, index
                        ) in feedbackFilter.searchAttributes"
                        v-bind:key="`feedback_${feedbackFilter.id}_${index}`"
                    >
                        <b-link
                            @click="
                                $_openLink({
                                    type: 'feedbackFilter',
                                    data: { feedbackFilter, searchAttribute }
                                })
                            "
                            v-ng-usage:click="{
                                name: 'searchResult',
                                type: 'feedbackFilter'
                            }"
                            :title="searchAttribute.value"
                            :aria-label="$t('searchResultLabelFeedbackFilter')"
                        >
                            <div class="icon_inline" v-if="!accessibleMode">
                                <div class="cube-icon"></div>
                            </div>
                            <span
                                v-html="
                                    $options.filters.highlight(
                                        searchAttribute.value,
                                        searchText
                                    )
                                "
                            ></span>
                            (<span class="theme-name">{{
                                feedbackFilter.name
                            }}</span
                            >)
                        </b-link>
                    </li>
                </ul>
                <!-- Addresses -->
                <ul
                    v-if="
                        searchSuggestions.addresses &&
                        searchSuggestions.addresses.length > 0
                    "
                >
                    <li
                        v-for="address in searchSuggestions.addresses"
                        v-bind:key="address.id"
                    >
                        <b-link
                            @click="
                                $_openLink({ type: 'address', data: address })
                            "
                            @focus="$_onFocusAddress(address.weergavenaam)"
                            v-ng-usage:click="{
                                name: 'searchResult',
                                type: 'address'
                            }"
                            :title="address.weergavenaam"
                            :aria-label="$t('searchResultLabelAdres')"
                        >
                            <div class="icon_inline" v-if="!accessibleMode">
                                <div class="location-icon"></div>
                            </div>
                            <span
                                v-html="
                                    $options.filters.highlight(
                                        address.weergavenaam,
                                        searchText
                                    )
                                "
                            ></span>
                        </b-link>
                    </li>
                </ul>
                <!-- Parcels -->
                <ul
                    v-if="
                        searchSuggestions.parcels &&
                        searchSuggestions.parcels.length > 0
                    "
                >
                    <li
                        v-for="(parcel, index) in searchSuggestions.parcels"
                        v-bind:key="index"
                    >
                        <b-link
                            @click="
                                $_openLink({ type: 'parcel', data: parcel })
                            "
                            v-ng-usage:click="{
                                name: 'searchResult',
                                type: 'parcel'
                            }"
                            :title="parcel"
                            :aria-label="$t('searchResultLabelParcel')"
                        >
                            <div class="icon_inline" v-if="!accessibleMode">
                                <div class="parcel-icon"></div>
                            </div>
                            {{ parcel }}
                        </b-link>
                    </li>
                </ul>
            </template>
            <!-- Coordinates -->
            <ul v-if="detectedPoint && hasSearchText">
                <li
                    @click="$_focusCoordinates(detectedPoint)"
                    v-ng-usage:click="{
                        name: 'searchResult',
                        type: 'coordinates'
                    }"
                >
                    <div class="icon_inline" v-if="!accessibleMode">
                        <div class="location-icon"></div>
                    </div>
                    {{ detectedPoint[0] }} {{ detectedPoint[1] }}
                </li>
            </ul>
            <!-- Search settings -->
            <ul v-if="!accessibleMode && this.$store.getters.isMobile">
                <li
                    @click="$_showSearchSettings"
                    v-ng-usage:click="{
                        name: 'searchResult',
                        type: 'SearchSettings'
                    }"
                    class="pl-2 advanced-search"
                >
                    <span>{{ $t('searchSettings.title') }}</span>
                    <span class="chevron-right-icon right"></span>
                </li>
            </ul>
            <!-- Advanced search -->
            <ul
                v-if="
                    (hasTableSearchResults ||
                        hasLayersWithFilters ||
                        hasFeedbacks ||
                        (userIsStufAuthorized &&
                            userIsAllowedStufPersonQueries) ||
                        userIsKvkAuthorized ||
                        userIsBrkAuthorized ||
                        userIsNedBrkAuthorized ||
                        userIsBrpAuthorized ||
                        userIsWozAuthorized) &&
                    !accessibleMode
                "
            >
                <li
                    @click="$_showAdvancedSearchModal"
                    v-ng-usage:click="{
                        name: 'searchResult',
                        type: 'advancedSearch'
                    }"
                    class="pl-2 advanced-search"
                >
                    <span>{{ $t('advancedSearchTitle') }}</span>
                    <span class="chevron-right-icon right"></span>
                </li>
            </ul>
            <!-- Saved usersearches -->
            <ul v-if="hasSavedUserSearches && !accessibleMode">
                <li
                    v-for="(search, index) in savedUserSearches"
                    v-bind:key="index"
                >
                    <b-link
                        @click="
                            $_openLink({ type: 'userSearch', data: search })
                        "
                        v-ng-usage:click="{
                            name: 'searchResult',
                            type: 'userSearch'
                        }"
                        :title="$t('userSearches.open')"
                    >
                        <div class="icon_inline">
                            <div class="slide_search"></div>
                        </div>
                        <span class="theme-name">{{ search.name }}</span>
                        <div
                            class="icon_inline float-right"
                            :title="$t('userSearches.delete')"
                            @click.stop="$_onDeleteUserSearch(search.id)"
                        >
                            <div class="close-icon"></div>
                        </div>
                    </b-link>
                </li>
            </ul>
        </div>
        <save-user-search
            v-if="saveUserSearchActive"
            :default-name="localSearchText"
            @create="$_onCreateUserSearch"
            @close="$_onCloseSaveUserSearch"
        />
    </div>
</template>

<script>
    import { debounce } from 'lodash';
    import { mapGetters, mapState } from 'vuex';

    import { INTERFACE_PANEL_TRIGGERS } from '../../../../constants';

    import SearchBox from '../../../SearchBox.vue';
    import SaveUserSearch from '../search/SaveUserSearch.vue';

    export default {
        name: 'MapSearchControl',

        components: {
            'search-box': SearchBox,
            'save-user-search': SaveUserSearch
        },

        props: {
            mode: { type: String, default: 'mobile' },
            searchText: { type: String, required: false, default: '' },
            searchSave: { type: Object, required: false, default: null },
            searchSuggestions: { type: Object, required: false, default: null },
            searchShowSuggestions: {
                type: Boolean,
                required: false,
                default: true
            },
            showSearchIcon: { type: Boolean, required: false, default: true }
        },

        filters: {
            highlight: function (str, term) {
                // Regex to simultaneously replace terms, case insensitive
                const pattern = new RegExp(`(${term})`, 'gi');
                return str.replace(
                    pattern,
                    '<span class="highlightText">$1</span>'
                );
            }
        },

        data() {
            return {
                panelTrigger: INTERFACE_PANEL_TRIGGERS.SEARCH_SETTINGS,
                currentLI: -1,
                localSearchText: null,
                detectedPoint: null,
                focussed: false,
                pressed: false,
                pressedTimeOut: null,
                showSaveButton: false,
                saveUserSearchActive: false,
                executeSearch: true // prevents the execution of the search action in case the text has been set by advanced search
            };
        },

        computed: {
            ...mapState([
                'userIsStufAuthorized',
                'userIsAllowedStufPersonQueries',
                'userIsKvkAuthorized',
                'userIsBrkAuthorized',
                'userIsNedBrkAuthorized',
                'userIsBrpAuthorized',
                'userIsWozAuthorized',
                'tableSearchResults',
                'uiElements'
            ]),
            ...mapGetters([
                'getSearchableWFSLayers',
                'getActivatedWFSLayers',
                'getUserSearches',
                'getActivatedFeedbacks',
                'isMobile'
            ]),

            searchSettingsPanel: function () {
                return this.uiElements[this.panelTrigger];
            },

            searchHasResults() {
                return this.searchSuggestions !== null;
            },
            hasSearchText() {
                return this.searchText !== null && this.searchText !== '';
            },
            hasLayersWithFilters() {
                const layersWithFilters = this.getSearchableWFSLayers;
                return layersWithFilters.length > 0;
            },
            hasFeedbacks() {
                return this.isMobile && this.getActivatedFeedbacks.length > 0;
            },
            hasTableSearchResults() {
                return this.tableSearchResults.length > 0;
            },
            hasSavedUserSearches() {
                return this.savedUserSearches?.length > 0;
            },
            savedUserSearches() {
                return this.getUserSearches?.filter(({ themeLayerId }) => {
                    return this.getActivatedWFSLayers.find(
                        (layer) => layer.data.id === themeLayerId
                    );
                });
            },
            displayResults() {
                return this.focussed || this.pressed;
            },
            accessibleMode() {
                return this.mode === 'accessible';
            }
        },

        mounted() {
            if (this.mode === 'accessible') {
                this.$refs.searchBoxInput.$refs.searchInput.$refs.inputField.focus();
            }
        },
        beforeDestroy() {
            this.$_stopListenIng();
        },

        methods: {
            $_startListenIng() {
                document.addEventListener('keyup', this.$_onKeyUp);
                document.addEventListener('click', this.$_onInteraction);
                this.$refs.searchContainer.addEventListener(
                    'click',
                    this.$_onClick
                );
            },
            $_stopListenIng() {
                clearTimeout(this.pressedTimeOut);
                document.removeEventListener('keyup', this.$_onKeyUp);
                document.removeEventListener('click', this.$_onInteraction);
                this.$refs.searchContainer?.removeEventListener(
                    'click',
                    this.$_onClick
                );
            },
            $_handleUpArrow(listItems) {
                // Decrease the counter
                if (this.currentLI > 0) {
                    this.currentLI--;
                } else {
                    this.currentLI = 0;
                }
                // Highlight the new element
                listItems[this.currentLI].focus();
            },

            $_handleDownArrow(listItems) {
                // Increase counter
                if (this.currentLI < listItems.length - 1) {
                    this.currentLI++;
                } else {
                    this.currentLI = listItems.length - 1;
                }

                // Highlight the new element
                listItems[this.currentLI].focus();
            },

            /**
             * Navigate through the search results with the up / down arrows
             * Select highlighted row when <enter> is pressed
             */
            $_onKeyUp: function (event) {
                const divElement = this.$refs.searchResults;
                if (!divElement) {
                    return;
                }

                const listItems = divElement.getElementsByTagName('a');

                if (listItems.length === 0) {
                    return;
                }

                switch (event.keyCode) {
                    case 13: // Enter
                        event.preventDefault();
                        if (this.detectedPoint) {
                            this.$_focusCoordinates(this.detectedPoint);
                        } else if (listItems.length > 0 && this.currentLI !== -1) {
                            listItems[this.currentLI].click();
                        }
                        break;
                    case 38: // Up arrow
                        this.$_handleUpArrow(listItems);
                        break;

                    case 40: // Down arrow
                        this.$_handleDownArrow(listItems);
                        break;
                    default:
                        this.$_onInteraction();
                }
            },

            $_onKeyDown(event) {
                // Prevent navigation on Enter. Let keyup event handel the event.
                if (event.keyCode === 13) {
                    event.preventDefault();
                }
            },

            $_onClick(event) {
                event.stopPropagation();
            },

            /**
             * Search handler with delay
             * @param {*} searchTerm user search input
             */
            $_search: debounce(function (searchTerm) {
                this.$emit('updateDebounced', searchTerm);
            }, 250),

            /**
             * Search field focus.
             */
            $_onFocus() {
                this.$_startListenIng();
                this.$emit('focus');
                this.focussed = true;

                if (this.hasSearchText && !this.searchHasResults) {
                    this.$emit('updateDebounced', this.searchText);
                }
            },
            $_onFocusAddress(text) {
                if (this.accessibleMode) {
                    this.$store.dispatch('say', { text });
                }
            },
            $_onInputBlur() {
                setTimeout(() => {
                    this.$_onInteraction();
                }, 20);
            },
            $_onInteraction() {
                const searchResultElements =
                    this.$refs.searchResults?.getElementsByTagName('a');
                const searchBox = document.getElementById('searchBox');
                const focusableElements = searchResultElements
                    ? [...searchResultElements]
                    : [];
                focusableElements.push(searchBox);
                const resultIsFocussed = focusableElements.find(
                    (element) => element === document.activeElement
                );

                if (resultIsFocussed) {
                    return;
                }
                this.$_stopListenIng();
                this.$emit('blur');
                this.focussed = false;
            },

            $_resultMouseDown(pressed) {
                if (pressed) {
                    this.pressed = pressed;
                } else {
                    //
                    this.pressedTimeOut = setTimeout(() => {
                        this.pressed = pressed;
                    }, 10);
                }
            },

            /**
             * When search results are added to the list, start again at the top with the arrow keys
             */
            $_resetKeyboardNavigation() {
                const divElement = this.$refs.searchResults;
                if (!divElement) {
                    return;
                }
                divElement.scrollTop = 0;
                this.currentLI = -1;
            },

            /*
             * Search text
             */
            $_onInput(textToSearch) {
                if (this.executeSearch) {
                    if (
                        !this.searchSettingsPanel ||
                        this.searchSettingsPanel.settings.coordinateSearch
                    ) {
                        this.$_searchCoordinates(textToSearch);
                    }
                    this.$emit('update', textToSearch);
                    this.$_search(textToSearch);
                }
            },

            $_searchCoordinates(textToSearch) {
                this.detectedPoint = null;
                let searchText = textToSearch.trim();

                // detects a number with possible point separator
                const coordinateRegex = new RegExp(/(\d{1,}\.{0,1}\d{1,6})/);
                // detects a possible separator character (doublepoint, comma or space)
                const separatorRegex = new RegExp(/(:|,| )/);
                // counts number of separotors to be able to change comma's in the coordinate into points
                const numberSeparatorOcurrences = [
                    ...searchText.matchAll(new RegExp(separatorRegex.source, 'g'))
                ];
                // changes comma's to points if multiple separators are found
                if (numberSeparatorOcurrences.length > 1) {
                    searchText = searchText.replace(/,/g, '.');
                }
                // detection of coordinates
                const coordinatesRegex = new RegExp(
                    coordinateRegex.source +
                        separatorRegex.source +
                        coordinateRegex.source,
                    'g'
                );
                const hasCoordinates = coordinatesRegex.test(searchText);

                if (hasCoordinates) {
                    const bbox = this.$store.getters.getOrganizationBBox;
                    const coordinates = [
                        ...searchText.matchAll(
                            new RegExp(coordinateRegex.source, 'g')
                        )
                    ];
                    const point = [
                        Number(coordinates[0][0]),
                        Number(coordinates[1][0])
                    ];

                    // a point needs to be in the organization bbox
                    const isValidPoint =
                        point[0] > bbox[0] &&
                        point[0] < bbox[2] &&
                        point[1] > bbox[1] &&
                        point[1] < bbox[3];

                    if (isValidPoint) {
                        this.detectedPoint = point;
                        return true;
                    }
                }
                return false;
            },

            async $_focusCoordinates(point) {
                this.$store.dispatch('customFocus', {
                    point,
                    x: 'center',
                    y: 'top'
                });
                this.$store.dispatch('setNedGlobePointer', { point });
                await this.$store.dispatch('prepareAddressDataAtPoint', {
                    point: point,
                    address: null
                });
                this.$store.commit('MAP_ADD_MARKER', { mapName: 'main', point });
                this.detectedPoint = null;
            },
            $_showSearchSettings() {
                this.$store.dispatch('togglePanel', {
                    panelName: INTERFACE_PANEL_TRIGGERS.SEARCH_SETTINGS,
                    visible: true
                });
            },
            $_showAdvancedSearchModal() {
                this.$store.commit('SET_USERSEARCH_FILTER_DATA', {});
                this.$store.dispatch('togglePanel', {
                    panelName: INTERFACE_PANEL_TRIGGERS.ADVANCED_SEARCH,
                    visible: true
                });
            },

            $_onSaveUserSearch() {
                this.saveUserSearchActive = true;
            },

            $_onCloseSaveUserSearch() {
                this.saveUserSearchActive = false;
            },

            $_onCreateUserSearch(name) {
                this.$_onCloseSaveUserSearch();
                this.showSaveButton = false;

                this.$store.dispatch('addUserSearch', {
                    name: name,
                    themeId: this.searchSave.themeId,
                    themeLayerId: this.searchSave.themeLayerId,
                    content: this.searchSave.content
                });
                this.$store.commit('CLEAR_NAVBAR_SEARCH_RESULTS');
            },

            $_onDeleteUserSearch(id) {
                this.$store.dispatch('deleteUserSearch', id);
            },

            $_openLink(data) {
                this.$emit('open', data);
                this.focussed = false;
            }
        },

        watch: {
            searchSuggestions(newValue, oldValue) {
                this.$_resetKeyboardNavigation();
            },

            searchShowSuggestions(newValue, oldValue) {
                this.$_resetKeyboardNavigation();
            },
            searchText: {
                handler: function (newVal) {
                    this.localSearchText = newVal;
                },
                immediate: true
            },

            async searchSave(newValue) {
                // prevent the execution of the search action during the setting of the searchtext
                // as a reaction to saving the advanced search result.
                this.executeSearch = false;
                if (newValue) {
                    await this.$store.commit('UPDATE_NAVBAR_SEARCH_SEARCHTEXT', {
                        searchText: `${newValue.content.layerTitle} (${newValue.content.featureCount})`
                    });
                } else {
                    await this.$store.commit('UPDATE_NAVBAR_SEARCH_SEARCHTEXT', {
                        searchText: ''
                    });
                }
                this.executeSearch = true;
                this.showSaveButton = true;
            }
        }
    };
</script>

<style lang="scss">
.search-bar-mobile input,
.search-bar-web-large input {
    background-color: #eeeeee;
}
.search-container-web input {
    border: 0;
}
.search-results {
    ul {
        margin-bottom: 0;
        li a {
            color: inherit;
            display: inline-block;
            width: 100%;
            &:hover {
                text-decoration: none;
            }
            &:focus {
                @include highlight-nav-item;
            }
        }
    }
}
.search-container-accessible .search-results-accessible {
    ul {
        margin-bottom: 0;
        li {
            .highlightText {
                text-decoration: underline;
                display: inline-block;
                color: black;
            }
            a:focus {
                background-color: black;
                color: white;
                font-size: 1.4em;
                line-height: unset;
                .highlightText {
                    color: white;
                    text-decoration: underline;
                    display: inline-block;
                }
            }
        }
    }
}
</style>

<style lang="scss" scoped>
.icon_inline {
    width: 30px;
    height: 30px;
    margin: 5px 3px 5px 3px;
    float: left;
    overflow: hidden;
    position: relative;
    & div {
        width: 100%;
        height: 100%;
        background-size: 24px;
        background-repeat: no-repeat;
        background-position: center;
        -webkit-mask-position: center;
        mask-position: center;
        -webkit-mask-repeat: no-repeat;
        mask-repeat: no-repeat;
        -webkit-mask-size: 24px;
        mask-size: 24px;
    }
    & .close-icon {
        -webkit-mask-size: 15px;
        mask-size: 15px;
    }
}

.search-bar-all {
    position: relative;
    margin-right: 0;
    margin-bottom: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.search-container-web {
    flex-grow: 1;
}
.search-container-accessible {
    flex-grow: 1;
}

.search-bar-mobile,
.search-bar-web-large {
    width: 420px;
}

.search-results {
    position: absolute;
    width: 420px;
    height: auto;
    max-height: 287px;
    background: #ffffff;
    overflow-y: auto;
    box-shadow: 1px 2px 6px rgba(0, 0, 0, 0.25);
    z-index: 21;
    overflow-x: hidden;

    & ul {
        list-style: none;
        width: 100%;

        & li {
            padding: 0px;
            line-height: 40px;
            white-space: nowrap;
            border-bottom: 1px solid #e6e6e6;
        }
    }

    .chevron-right-icon {
        width: 22px;
        height: 22px;
        background: url('~@/assets/imported-icons/ic_fluent_chevron_right_20_regular.svg')
            no-repeat center center;
        margin-top: 8px;
        margin-right: 6px;
    }
}

/* Mobiles/Tablets */
@media (max-width: 767px) {
    .search-bar-mobile {
        width: 200px;
    }

    .search-bar-web-large {
        width: 100%;
    }
}

/* Laptops */
@media (min-width: 768px) and (max-width: 1024px) {
    .search-bar-web-large {
        width: 200px;
    }
}

/* Mobiles */
@media (max-width: 480px) {
    .search-results.search-results-web {
        min-width: unset;
        max-width: unset;
        width: 100%;
    }
}
</style>
